import { EventTarget as EventTargetShim } from "event-target-shim";

import { detectPitch } from "./soundAnalyser";
import { Sound } from "./core";
import { isNumber } from "lodash";
import {
  MAX_SOUND_DURATION,
  ANALYSER_NUM_SPECTRAL_FREQUENCIES,
  ANALYSER_SMOOTHING
} from "./constants";

const impulseResponseUrl = require("./assets/scala-milan-opera-hall.wav");
/* eslint import/no-webpack-loader-syntax: "off" */

let AContext: any;
if ("AudioContext" in window) {
  AContext = (window as any).AudioContext;
} else {
  AContext = (window as any).webkitAudioContext;
}

export const audioCtx: AudioContext = new AContext();

export const impulseResponse = fetch(impulseResponseUrl)
  .then(res => res.arrayBuffer())
  .then(
    buf => new Promise((res, rej) => audioCtx.decodeAudioData(buf, res, rej))
  )
  .catch(err => console.error(err));

export const analyser = audioCtx.createAnalyser();
analyser.fftSize = ANALYSER_NUM_SPECTRAL_FREQUENCIES * 2;
analyser.smoothingTimeConstant = ANALYSER_SMOOTHING;

export function getMicrophoneStream(): Promise<MediaStream> {
  if (navigator.mediaDevices) {
    return navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false
    });
  } else {
    return new Promise((res, rej) =>
      navigator.getUserMedia({ audio: true, video: false }, res, rej)
    );
  }
}

export class Recorder extends EventTargetShim {
  streamSource?: MediaStreamAudioSourceNode;
  private recorder?: MediaRecorder;
  private chunks?: Blob[];
  private recordingStartedAt?: number;
  private stopper?: NodeJS.Timeout;

  constructor(
    private inStream: MediaStream,
    private onStop: (sound: Sound) => void
  ) {
    super();
  }

  async start() {
    this.recorder = new MediaRecorder(this.inStream);
    this.chunks = [];
    this.recorder.addEventListener("dataavailable", evt =>
      (this.chunks as Blob[]).push((evt as BlobEvent).data)
    );
    this.recorder.addEventListener("start", () =>
      this.dispatchEvent(new Event("start"))
    );
    this.streamSource = audioCtx.createMediaStreamSource(this.inStream);
    this.streamSource.connect(analyser);
    (this.recorder as MediaRecorder).start();
    this.recordingStartedAt = audioCtx.currentTime;
    this.stopper = setTimeout(() => {
      this.stop();
    }, MAX_SOUND_DURATION * 1000);
  }

  stop() {
    let recorder = this.recorder as MediaRecorder;
    recorder.addEventListener("stop", async () => {
      let sound = await blobsToSound(this.chunks as Blob[]);
      this.recorder = undefined;
      this.chunks = undefined;
      this.recordingStartedAt = undefined;
      this.stopper = undefined;
      (this.streamSource as AudioNode).disconnect();
      this.onStop(sound);
    });
    recorder.stop();
    for (let track of this.inStream.getAudioTracks()) {
      track.stop();
    }
    clearTimeout(this.stopper as NodeJS.Timeout);
  }

  get recordedDuration() {
    if (isNumber(this.recordingStartedAt)) {
      return audioCtx.currentTime - this.recordingStartedAt;
    } else {
      return 0;
    }
  }
}

export class Player {
  sourceNode?: AudioBufferSourceNode;
  private playbackStartedAt?: number;

  constructor(public sound: Sound) {}

  start() {
    this.sourceNode = audioCtx.createBufferSource();
    this.sourceNode.buffer = this.sound.data;
    this.sourceNode.loop = true;
    this.sourceNode.connect(audioCtx.destination);
    this.sourceNode.start();
    this.sourceNode.connect(analyser);
    this.playbackStartedAt = audioCtx.currentTime;
  }

  stop() {
    if (this.sourceNode) {
      this.sourceNode.stop();
      this.sourceNode.disconnect();
      this.sourceNode = undefined;
    }
  }

  get playedDuration() {
    if (isNumber(this.playbackStartedAt) && this.sourceNode) {
      let buffer = this.sourceNode.buffer as AudioBuffer;
      return (audioCtx.currentTime - this.playbackStartedAt) % buffer.duration;
    } else {
      return 0;
    }
  }
}

function blobsToSound(blobs: Blob[]): Promise<Sound> {
  return new Promise((res, rej) => {
    let superBlob = new Blob(blobs, { type: blobs[0].type });
    let fileReader = new FileReader();
    fileReader.onloadend = () =>
      new Promise<AudioBuffer>((res, rej) =>
        audioCtx.decodeAudioData(fileReader.result as ArrayBuffer, res, rej)
      )
        .then(buildSound)
        .then(res);
    fileReader.onerror = rej;
    fileReader.readAsArrayBuffer(superBlob);
  });
}

function buildSound(data: AudioBuffer) {
  let pitch = `${detectPitch(data)}`;
  return {
    data,
    metadata: { pitch }
  };
}

let freezer = new Promise<any>((res, rej) => {
  setTimeout(
    () =>
      import("robot-choir-sound-freezer")
        .then(f => res(f.freeze))
        .catch(rej),
    3000
  );
});

export async function freezeSound(
  sound: Sound,
  fromTime: number,
  toTime: number,
  freezeLength: number
): Promise<Sound> {
  let freeze = await freezer;
  let freezeSamples = Math.floor(freezeLength * sound.data.sampleRate);
  let fromSample = Math.floor(fromTime * sound.data.sampleRate);
  let toSample = Math.floor(toTime * sound.data.sampleRate);
  let inData = new Float32Array(freezeSamples);
  inData.set(
    sound.data
      .getChannelData(0)
      .subarray(0, Math.min(freezeSamples, sound.data.length))
  );
  let out = audioCtx.createBuffer(1, freezeSamples, sound.data.sampleRate);
  let outData = out.getChannelData(0);
  freeze(inData, outData, fromSample, toSample);
  return { ...sound, data: out };
}

export function reverseSound(sound: Sound) {
  let buffer = sound.data;
  let reversed = audioCtx.createBuffer(
    buffer.numberOfChannels,
    buffer.length,
    buffer.sampleRate
  );
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    let data = buffer.getChannelData(ch);
    let reversedData = reversed.getChannelData(ch);
    reversedData.set(data);
    reversedData.reverse();
  }
  return { ...sound, data: reversed };
}
